import { createPortal } from 'react-dom';

import { cx } from '@libs/classnames';
import useLockBodyScroll from '@libs/hooks/useLockBodyScroll';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';
import { CrossIcon } from '@ui/icons/CrossIcon';

import { FancyPanel, FancyPanelProps } from './FancyPanel';
import { FancyPanelControl } from './FancyPanelControl';

import styles from './FancyModal.module.css';

export type FancyModalPanelProps = Omit<FancyPanelProps, 'controls'> & {
  onClose: () => void;
};

export type FancyModalProps = FancyModalPanelProps & {
  // сверху: высота панели меняется с содержимым, и центрированная модалка прыгала бы
  align?: 'top' | 'center';
  overlayClassName?: string;
};

// Модалка на FancyPanel: оверлей, фон, крестик в углу. Фон, крестик и Escape закрывают.
// Рендерится в body, как Modal: тема и цвета клуба заданы на корне документа и доходят и туда;
// страница под модалкой не прокручивается
export function FancyModal({
  align = 'top',
  overlayClassName,
  onClose,
  ...panel
}: FancyModalProps) {
  useOnEscape(onClose);
  useLockBodyScroll(true);

  return createPortal(
    <div
      className={cx(
        styles.overlay,
        { [styles.is_top]: align === 'top', [styles.is_center]: align === 'center' },
        overlayClassName,
      )}
    >
      <div className={styles.backdrop} onClick={onClose} />
      <FancyModalPanel {...panel} onClose={onClose} />
    </div>,
    document.body,
  );
}

// Сама панель с крестиком, без оверлея: для сторибуков
export function FancyModalPanel({
  className,
  contentClassName,
  onClose,
  ...rest
}: FancyModalPanelProps) {
  return (
    <FancyPanel
      className={cx(styles.panel, className)}
      contentClassName={cx(styles.content, contentClassName)}
      controls={
        <FancyPanelControl aria-label="Закрыть" onClick={onClose}>
          <CrossIcon />
        </FancyPanelControl>
      }
      {...rest}
    />
  );
}
